import React, { useRef, useMemo, useEffect, forwardRef, useState } from 'react';
import Picture from '../../components/Picture/Picture';
import { observer } from 'mobx-react-lite';

export default observer(props => {
  const imageRefs = useRef({});
  const imageColumnRefs = useRef({});
  const [cols, setCols] = useState([]);

  const {
    settings,
    eventId,
    contentLayoutModuleId,
    contentModuleId,
    environment,
    selectedModule,
    w: moduleWidth,
    isSmartphone,
    replaceTags,
    ordersLoaded,
  } = props;
  const { columns, horizontalGroup, horizontalColumns: _horizontalColumns, lockedImageSize, imageMaxWidth } = settings;

  useEffect(() => {
    async function replaceText() {
      const parsedColumns = await Promise.all(
        columns?.map(async column => {
          const { text } = column;
          const hasTags = text?.[environment.lang]?.match(/\[[^\]]+\]/g);
          if (hasTags) {
            const replacedText = await replaceTags(text[environment.lang]);
            column.text[environment.lang] = replacedText;
          }
          return column;
        }) ?? []
      );
      setCols(parsedColumns);
    }
    if (ordersLoaded) {
      replaceText();
    } else {
      setCols(columns);
    }
  }, [columns, environment.lang, ordersLoaded, replaceTags]);

  const viewWidth = useMemo(() => `calc((100vw - 320px) * (${moduleWidth} / (12*${columns?.length})))`, [moduleWidth]);

  const isHidden = useMemo(
    () => columns?.filter(({ showImage }) => !showImage)?.length === columns?.length && columns.filter(({ showText }) => !showText)?.length === columns?.length,
    []
  );

  if (isHidden) return null;

  const getMinHeight = (imageRefs, columnRef) => {
    return Math.max(
      ...Object.values(imageRefs)?.map(element => {
        const { offsetHeight, id } = element;

        const columnIndex = id?.split('_')[2];
        const computedStyle = window.getComputedStyle(element);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        const paddingBottom = parseFloat(computedStyle.paddingBottom);
        let baseHeight = offsetHeight + paddingTop + paddingBottom;

        if (!columns[columnIndex]?.horizontalImages) {
          const imageLength = columns[columnIndex]?.images?.length;
          baseHeight = imageLength * baseHeight ?? 0;
        }

        if (columnRef[columnIndex]) {
          const computedColumnStyle = window.getComputedStyle(columnRef[columnIndex]);
          baseHeight += parseFloat(computedColumnStyle.paddingTop) + parseFloat(computedColumnStyle.paddingBottom);
        }

        return baseHeight ?? 0;
      })
    );
  };

  return (
    <div
      className="cms-content-group"
      style={{
        display: 'flex',
        width: '100%',
        flexDirection: horizontalGroup && !isSmartphone ? 'row' : 'column',
      }}
    >
      {cols?.map(
        (
          {
            images,
            text,
            showImage = true,
            justifyContent = 'center',
            alignItems = 'center',
            showText,
            textColWidth,
            overriddenAspectRatio,
            overrideAspectRatio,
            horizontalColumns,
            columnWidth,
            reversed,
            horizontalImages,
            imageColWidth,
          },
          index
        ) => (
          <div
            className="group-column"
            key={`image-group_${index}_${contentLayoutModuleId}`}
            style={{
              display: 'flex',
              width: horizontalGroup && !isSmartphone ? columnWidth ?? `calc(100%  / ${columns?.length})` : isSmartphone ? '100%' : columnWidth ?? 'auto',
              flexDirection:
                (horizontalColumns || _horizontalColumns) && !isSmartphone ? (reversed ? 'row-reverse' : 'row') : reversed ? 'column-reverse' : 'column',
              justifyContent: horizontalColumns || _horizontalColumns ? alignItems : justifyContent,
              alignItems: horizontalColumns || _horizontalColumns ? justifyContent : alignItems,
            }}
          >
            {showImage && (
              <div
                ref={ref => (imageColumnRefs.current[index] = ref)}
                className="image-column"
                style={{
                  height: imageRefs.current?.length > 1 ? getMinHeight(imageRefs.current, imageColumnRefs.current) : undefined,
                  width: !showText || isSmartphone ? '100%' : imageColWidth ?? '100%',
                  display: 'flex',
                  alignItems,
                  justifyContent,
                  flexDirection: horizontalImages ? 'row' : 'column',
                }}
              >
                {images?.map((image, imageIndex) => (
                  <Image
                    key={imageIndex}
                    image={image}
                    columns={images?.length}
                    columnIndex={index}
                    imageIndex={imageIndex}
                    overriddenAspectRatio={overriddenAspectRatio}
                    overrideAspectRatio={overrideAspectRatio}
                    contentModuleId={contentModuleId}
                    isSelected={selectedModule === contentModuleId}
                    alt={image?.alt ?? "Image text"}
                    eventId={eventId}
                    lockedImageSize={lockedImageSize}
                    imageMaxWidth={imageMaxWidth}
                    viewWidth={viewWidth}
                    ref={imageRefs}
                  />
                ))}
              </div>
            )}
            {showText && (
              <div className="text-column cms-text-content" style={{ width: !showImage ? '100%' : textColWidth ?? '100%' }}>
                <div
                  // className="overflow-auto"
                  dangerouslySetInnerHTML={{
                    __html: text?.[environment?.lang] ?? '',
                  }}
                />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
});

const Image = forwardRef(
  ({ image, columnIndex, imageIndex, lockedImageSize, imageMaxWidth, viewWidth, alt, contentModuleId, overrideAspectRatio, overriddenAspectRatio }, ref) => {
    const imageRef = useRef();

    const imageStyle = {
      maxWidth: '100%',
      maxHeight: '100%',
      display: 'flex',
      height: ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight > ref.current?.[columnIndex]?.[imageIndex]?.naturalWidth ? '100%' : undefined,
      width: ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight > ref.current?.[columnIndex]?.[imageIndex]?.naturalWidth ? undefined : '100%',
      aspectRatio:
        overrideAspectRatio && overriddenAspectRatio && (/\d+ ?\/ ?\d+/.test(overriddenAspectRatio) || overriddenAspectRatio === '1')
          ? overriddenAspectRatio
          : `${ref?.[columnIndex]?.[imageIndex]?.naturalWidth} / ${ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight}`,
    };

    useEffect(() => {
      ref.current[`col_${columnIndex}_image_${imageIndex}`] = imageRef.current;
    }, [columnIndex, imageIndex, ref, imageRef]);

    if (image?.link) {
      return (
        <a href={image.link} target={image.target ?? '_blank'}>
          <div style={{ position: 'relative', width: lockedImageSize ? `${imageMaxWidth}%` : '100%', maxHeight: '100%' }}>
            <Picture
              id={`${contentModuleId}_col_${columnIndex}`}
              src={image.src}
              srcset={image.srcset}
              alt={image?.alt ?? "Image text"}
              viewWidth={viewWidth}
              style={imageStyle}
              ref={imageRef}
            />
          </div>
        </a>
      );
    }

    return (
      <div style={{ position: 'relative', width: lockedImageSize ? `${imageMaxWidth}%` : '100%', maxHeight: '100%' }}>
        <Picture id={`${contentModuleId}_col_${columnIndex}`} alt={alt ?? "Image text"} src={image.src} srcset={image.srcset} viewWidth={viewWidth} style={imageStyle} ref={imageRef} />
      </div>
    );
  }
);
